import { Component, OnInit, AfterViewChecked, ElementRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { finalize, take } from 'rxjs/operators';
import { PaymentDataService } from '../../../services/payment.service';
import { PaymentCharge } from '../../models/paymentcharge';
import { ApplicationFee } from '../../models/applicationfee';
import { Application } from '../../models/application';
import { OtherApplicant } from '../../models/otherapplicant';
import { ApplicationInvites } from '../../models/applicationinvites';
import { CouponValidity } from '../../models/coupon-validity';
import { ApplicationDataService } from '../../../services/application.service';
import { Property } from '../../models/property';
import { EventEmitterService } from 'services/event-emitter.service';
import { SplashService } from 'services/splash.service';
import { environment } from '../../../environments/environment';
import { ErrorService } from 'services/error.service';
import { ApplicationInterface } from 'app/interface/application';
import { AnalyticsService } from 'services/analytics.service';
import { FeatureFlagService } from 'services/feature-flag.service';
import { SurveyMetadataService } from 'services/survey-metadata.service';
import { SurveyCookieService } from 'services/survey-cookie.service';

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.css']
})

export class PaymentComponent implements OnInit, AfterViewChecked {
  // Payment status constants
  private readonly PaymentStatus = {
    SUCCEEDED: 'succeeded',
    DUPLICATE: 'Duplicate Payment',
    INELIGIBLE: 'Ineligible'
  };

  @ViewChild('paymentInfo', { static: true }) private myScrollContainer: ElementRef;
  showTerms: boolean;
  hidePayment: boolean;
  errorMessage: string;
  allApplicantsComplete: boolean;
  incompleteApplicantWarningMessageNames: string;
  incompleteInvitesWarningMessageNames: string;
  paymentCheckoutFormMessage: string;
  otherApplicants: OtherApplicant[];
  rentalApplicationKey: string;
  rentalApplicantKey: string;
  primaryApplicant: string;
  couponCode: string = '';
  origTotalApplicationFee: number;
  propertyId: string;
  mfUnitCode: string;
  stripePublishableKey: string;
  ineligible: boolean;
  coupon: CouponValidity;
  property: Property;
  backgroundCheckNotVerified: Boolean;
  monthlyRentWarningMessage: string;
  public modelCharge: PaymentCharge;
  public modelApplicationFee: ApplicationFee;
  public modelApplicationInvites: ApplicationInvites;
  modelProperty: Property;
  splashHTML: string;
  applicantName: string = "";
  // TP-9217: The marketing success page that hosts the Medallia embed reader
  // lives on the triconhomes.com registrable domain (triconresidential.com
  // 301-redirects there, crossing registrable domains and dropping any cookie).
  // The host varies by environment (prod -> triconhomes.com, non-prod ->
  // staging.triconhomes.com) but stays within `.triconhomes.com`, so the survey
  // cookie's `.triconhomes.com` Domain still matches the destination.
  REDIRECT_URL: string = environment.applicationSubmissionSuccessUrl;
  paymentProcessing: boolean = false;
  hasRentableItems: boolean;
  totalApplicationSteps: number = 8;
  appStepIncrement: number = 0;
  verifastEnabled: boolean;

  /**
   * TP-9217: In-flight survey metadata fetch kicked off when the Stripe modal
   * opens. Awaited on payment success so the Medallia bridge cookie is only
   * written for actually-completed payments (no stale cookies on modal abandon).
   */
  private surveyMetadataPromise: Promise<unknown | null> | null = null;

  constructor(
    private paymentService: PaymentDataService,
    private applicationService: ApplicationDataService,
    private router: Router,
    private eventEmitterService: EventEmitterService,
    private splashService: SplashService,
    private errorService: ErrorService,
    private readonly analyticsService: AnalyticsService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly surveyMetadataService: SurveyMetadataService,
    private readonly surveyCookieService: SurveyCookieService) {
    this.analyticsService.track(
      'HRA Application',
      'Payment Information',
      'Start',
      'Start',
      '',
      this.additionalAnalyticsDetails());
    localStorage.removeItem('AddAdditionalApplicantKey');
    localStorage.removeItem('AddAdditionalApplicantName');

    const correctStatus = localStorage.getItem('CorrectStatus');

    if (correctStatus != "true") {
      localStorage.removeItem('RentalApplicationKey');
      this.analyticsService.track(
        'HRA Application',
        'Payment Information',
        'Stop',
        'Application not in the correct status',
        '/application/dashboard',
        this.additionalAnalyticsDetails());
      this.router.navigate(['/application/dashboard']);
    }

    this.modelCharge = new PaymentCharge();
    this.modelApplicationFee = new ApplicationFee();
    this.modelApplicationInvites = new ApplicationInvites();
    this.coupon = new CouponValidity();
    this.rentalApplicationKey = localStorage.getItem('RentalApplicationKey');
    this.backgroundCheckNotVerified = true;
    this.applicationService.getApplicationPrimaryApplicantName(this.rentalApplicationKey)
      .subscribe(res => this.applicantName = res, err => this.handleError(err));

    this.rentalApplicantKey = localStorage.getItem('RentalApplicantKey');
    this.primaryApplicant = localStorage.getItem('PrimaryApplicant');
    this.propertyId = localStorage.getItem('YardiKey');
    this.mfUnitCode = localStorage.getItem('mfunitcode');

    this.hidePayment = false;
    this.ineligible = false;
    const property = JSON.parse(localStorage.getItem('SelectedProperty'));
    if (property != null) {
      this.property = property[0];
      if ((property[0].min_rent != null && property[0].max_rent != null) && (property[0].min_rent == property[0].max_rent)) {
        this.property.rent = property[0].min_rent.toLocaleString();
      }
      else if (property[0].min_rent != null && property[0].max_rent != null) {
        this.property.rent = property[0].min_rent.toLocaleString() + '-$' + property[0].max_rent.toLocaleString()
      }
    }

    if (this.rentalApplicationKey === null
      || this.rentalApplicationKey === undefined
      || this.rentalApplicantKey === null
      || this.rentalApplicantKey === undefined
      || this.propertyId === null
      || this.propertyId === undefined) {
        this.analyticsService.track(
          'HRA Application',
          'Payment Information',
          'Stop',
          'Application or applicant not in the right state',
          '/application/dashboard',
          this.additionalAnalyticsDetails());
      this.router.navigate(['/application/dashboard', 'error']);
    }

    this.applicationService.verifyBackgroundCheck(this.rentalApplicationKey, this.rentalApplicantKey).subscribe(res => this.verifyBackgroundCheck(res), err => this.backgroundCheckError(err));

    this.eventEmitterService.splashChangeEmitted$.subscribe(res => this.updateSplashHTML(res));
    this.splashService.updateSplash("apply");

    if (this.primaryApplicant != "true") {
      this.analyticsService.track(
        'HRA Application',
        'Payment Information',
        'Stop',
        'Not the primary applicant',
        '/application/basic',
        this.additionalAnalyticsDetails());
      this.router.navigate(['/application/basic']);
    }
  }

  private additionalAnalyticsDetails(): any {
    try {
      return {
        'CouponCode': this.couponCode,
        'AmountWaived': this.modelApplicationFee?.AmountWaived,
        'ApplicantFee': this.modelApplicationFee?.ApplicantFee,
        'ApplicationFee': this.modelApplicationFee?.ApplicationFee,
        'TotalApplicationFee': this.modelApplicationFee?.TotalApplicationFee,
        'WaivingFee': this.modelApplicationFee?.WaivingFee
      };
    } catch (error) {
      console.error(error);
      return {};
    }
  }

  ngOnInit() {
    const rentableItems = localStorage.getItem('hasRentableItems');
    const verifastEnabled = localStorage.getItem('verifastEnabled');
    const hasAncillaryItems = localStorage.getItem('HasAncillaryItems');
    if ((rentableItems && rentableItems === 'true') || (hasAncillaryItems && hasAncillaryItems === 'true')) {
      this.hasRentableItems = true;
      this.totalApplicationSteps = 9;
      this.appStepIncrement = 1;
    }

    if (verifastEnabled && verifastEnabled === 'true') {
      this.verifastEnabled = true;
      this.totalApplicationSteps++;
      this.appStepIncrement++;
    }

    this.paymentService.getWaitListCheck(this.rentalApplicationKey).subscribe(res => this.handleWaitListCheck(res));

    this.paymentService.getApplicationInviteDetails(this.rentalApplicationKey)
      .subscribe(res => this.setIncompleteInvitesWarningMessage(res), err => this.handleError(err));;

    this.paymentService.getApplicationFeeWithDetails(this.rentalApplicationKey)
      .subscribe(res => this.setIncompleteApplicantsWarningMessage(res), err => this.handleError(err));

    this.paymentService.getStripePublishableKey(this.propertyId).subscribe(res => this.getStripePublishableKey(res),
      err => this.handleError(err));

    const property = JSON.parse(localStorage.getItem('SelectedProperty'));
    if (property != null) {
      this.modelProperty = property[0];
      if ((property[0].min_rent != null && property[0].max_rent != null) && (property[0].min_rent == property[0].max_rent)) {
        this.modelProperty.rent = property[0].min_rent.toLocaleString();
      }
      else if (property[0].min_rent != null && property[0].max_rent != null) {
        this.modelProperty.rent = property[0].min_rent.toLocaleString() + '-$' + property[0].max_rent.toLocaleString()
      }
    }
  }

  updateSplashHTML(res: string) {
    this.splashHTML = res;
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  scrollToBottom(): void {
    if (this.myScrollContainer) {
      this.myScrollContainer?.nativeElement.scrollIntoView(false);
    }
  }

  onShowTermsClick() {
    this.showTerms = true;
  }

  createRedirectUrl(unitcode: string, firstname: string, property: Property, mfUnitCode: string) {
    if (this.mfUnitCode !== null) {
      return `${this.REDIRECT_URL}?unitcode=${mfUnitCode}&property_id=${unitcode}`;
    }
    else {
      return `${this.REDIRECT_URL}?unitcode=${unitcode}`;
    }
  }

  verifyBackgroundCheck(hasBackgroundCheck: boolean) {
    if (!hasBackgroundCheck) {
      this.analyticsService.track(
        'HRA Application',
        'Payment Information',
        'Stop',
        'No background check',
        '/application/background-check',
        this.additionalAnalyticsDetails());
      this.router.navigate(['/application/background-check']);
    } else {
      this.backgroundCheckNotVerified = false;
    }
  }

  backgroundCheckError(err: any) {
    this.errorService.showError(err);
    this.analyticsService.track(
      'HRA Application',
      'Payment Information',
      'Stop',
      'Error during background check',
      '/application/background-check',
      this.additionalAnalyticsDetails());
    this.router.navigate(['/application/background-check']);
  }

  onSubmit(paymentResponse: string) {
    switch (paymentResponse) {
      case this.PaymentStatus.SUCCEEDED:
      case this.PaymentStatus.DUPLICATE:
        this.handleSuccessfulPayment();
        break;
      
      case this.PaymentStatus.INELIGIBLE:
        this.handleErrorForDiscount();
        break;
      
      default:
        this.handleError(paymentResponse);
    }
  }

  private handleSuccessfulPayment(): void {

    // Use finalize to ensure redirect happens after API call completes or fails
    this.applicationService.publishRentalApplicationCompletedEvent(Number(this.rentalApplicationKey))
      .pipe(
        finalize(() => {
          const redirectURL = this.createRedirectUrl(
            this.propertyId,
            this.applicantName,
            this.property,
            this.mfUnitCode
          );
          // TP-9217: Await the in-flight survey metadata fetch (kicked off when
          // the Stripe modal opened), write the Medallia bridge cookie iff a
          // payload arrived, then clear localStorage and redirect. The fetch
          // never rejects, so the redirect always happens.
          this.writeSurveyCookieThenRedirect(redirectURL);
        })
      )
      .subscribe(
        () => {}, // Success handled in finalize
        err => {
          // Log error but still redirect (finalize will execute)
          console.error('Error marking application as completed:', err);
        }
      );
  }


  handleError(error: Response | any) {
    this.paymentProcessing = false;
    if (error.status === 403) {
      this.analyticsService.track(
        'HRA Application',
        'Payment Information',
        'Stop',
        'Error: 403',
        '/login',
        this.additionalAnalyticsDetails());
      this.router.navigate(['/login']);
    } else {
      let errMsg: string;
      errMsg = 'We’re sorry, but we are unable to process your payment at this time. '
      errMsg += 'Please call our customer service line at 844.874.2661 for assistance.'
      this.errorMessage = errMsg;
      this.hidePayment = true;
    }
  }

  handleErrorForPayment(res) {
    this.paymentProcessing = false;
    if (res.status === 403) {
      this.analyticsService.track(
        'HRA Application',
        'Payment Information',
        'Stop',
        'Error: payment 403',
        '/login',
        this.additionalAnalyticsDetails());
      this.router.navigate(['/login']);
    } else if (res.status === 400) {
      this.errorService.showError(res.error?.Message ?? 'Bad Request')
    } else if (res.status === 500) {
      this.errorService.showError(res.error?.ExceptionMessage ?? '500 ');
    } else {
      this.errorService.showError(`${res.status} ${res.error?.Message ?? 'Error unknown'}`)
    }
  }

  handleErrorForDiscount() {
    this.paymentProcessing = false;
    let errMsg: string;
    errMsg = 'There was an error processing your payment. You are no longer eligible for the waived applicant fee. Press Submit Payment again to submit a payment for the full fee amount.'
    this.errorMessage = errMsg;
  }

  getStripePublishableKey(res: string) {
    this.stripePublishableKey = res;
  }

  getApplicationFeeWithDetails() {
    this.paymentService.getApplicationFeeWithDetails(this.rentalApplicationKey).subscribe(res => this.getApplicationFeeInfo(res),
      err => this.handleError(err));
  }

  updateMonthlyRentForProperty() {
    this.paymentService.updateMonthlyRentForProperty(this.rentalApplicationKey),
      err => this.handleError(err);
  }

  getApplicationFeeInfo(fees: ApplicationFee) {
    this.modelApplicationFee = fees;
    this.setCheckoutFormMessage();
  }

  setIncompleteApplicantsWarningMessage(fees: ApplicationFee) {
    this.modelApplicationFee = fees;

    if (this.modelApplicationFee.AllApplicantsComplete === false) {
      const firstNames = <OtherApplicant[]>this.modelApplicationFee.ApplicantsNotCompleteFirstNames;
      const namesForMessage = firstNames.join(' & ');
      this.incompleteApplicantWarningMessageNames = namesForMessage;
    }
  }

  setMonthlyRentWarningMessage(rent: number) {
    if (Number(this?.property?.rent) !== rent) {
      this.monthlyRentWarningMessage = 'The monthly rent has changed from $' + rent + ' to $' + this.property.rent;
    }
  }

  setIncompleteInvitesWarningMessage(invites: ApplicationInvites) {
    this.modelApplicationInvites = invites;
    if (this.modelApplicationInvites.AllInvitesComplete === false) {
      const firstNames = <OtherApplicant[]>this.modelApplicationInvites.InvitesNotCompleteFirstNames;
      const namesForMessage = firstNames.join(' & ');
      this.incompleteInvitesWarningMessageNames = namesForMessage;
    }
  }

  setCheckoutFormMessage() {
    const firstNames = <OtherApplicant[]>this.modelApplicationFee.AllApplicantsFirstNames;
    const namesForMessage = firstNames.join(' & ');
    this.paymentCheckoutFormMessage = 'You are paying for ' + namesForMessage;
  }

  processCharge(token: any) {
    this.paymentProcessing = true;
    this.modelCharge.ChargeToken = token.id;
    this.modelCharge.ChargeAmount = this.modelApplicationFee.TotalApplicationFee;
    this.modelCharge.ChargeCurrency = 'usd';
    this.modelCharge.ChargeEmail = token.email;
    this.modelCharge.PropertyId = this.propertyId;
    this.paymentService.createStripeCharge(this.modelCharge,
      this.rentalApplicationKey,
      true,
      this.modelApplicationFee.WaivingFee,
      this.couponCode
    )
      .subscribe(res => this.onSubmit(res), res => this.handleErrorForPayment(res));
  }

  getApplicationFeeInfoForCheckout(fees: ApplicationFee) {
    this.modelApplicationFee = fees;
    this.setCheckoutFormMessage();

    if (this.origTotalApplicationFee !== this.modelApplicationFee.TotalApplicationFee) {
      this.errorMessage = 'The payment amount has changed. Please confirm the updated payment amount and then click Submit Payment.';
    } else {
      this.openCheckout();
    }
  }

  startCheckout() {
    this.analyticsService.track(
      'HRA Application',
      'Payment Information',
      'Payment model - Open',
      'Opening the payment model',
      '',
      this.additionalAnalyticsDetails());
    this.applicationService.GetIncomeVerificationMessageForApplication(this.rentalApplicationKey)
      .subscribe(res => {
        if (res == "") {
          this.analyticsService.track(
            'HRA Application',
            'Payment Information',
            'Payment model - Close',
            'Completed the payment model',
            '',
            this.additionalAnalyticsDetails());
          this.applicationService.checkIsPrimary(Number(this.rentalApplicationKey))
            .subscribe(res => this.onPrimaryResponse(res), err => this.handleError(err));
        } else {
          this.analyticsService.track(
            'HRA Application',
            'Payment Information',
            'Payment model - Error',
            'Error in the payment model',
            '',
            this.additionalAnalyticsDetails());
          this.errorService.showError(res);
        }
      }, err => this.applicationService.handleError(err));
  }

  onPrimaryResponse(res: boolean) {
    if (res == true) {
      // get payment fees again prior to charge to confirm no changes
      this.origTotalApplicationFee = this.modelApplicationFee.TotalApplicationFee;
      this.paymentService.getApplicationFeeWithDetails(this.rentalApplicationKey, this.couponCode).subscribe(res => this.getApplicationFeeInfoForCheckout(res),
        err => this.handleError(err));
    }
    else {
      this.analyticsService.track(
        'HRA Application',
        'Payment Information',
        'Stop',
        'Not the primary resident, sending to basic information',
        '/application/basic',
        this.additionalAnalyticsDetails());
      this.router.navigate(['/application/basic']);
    }
  }

  private async startSurveyMetadataFetch(): Promise<unknown | null> {
    // TP-9217: Non-blocking metadata fetch. Every failure path resolves to
    // null so the awaiting redirect logic in writeSurveyCookieThenRedirect
    // never sees a rejection and the user always reaches the success page.
    try {
      const enabled = await this.featureFlagService.isEnabled('enable-application-survey', false);
      if (!enabled || !this.rentalApplicationKey) {
        return null;
      }

      // take(1) completes the stream after the first emission so toPromise()
      // resolves immediately and the subscription is torn down (RxJS 6 has no
      // firstValueFrom; toPromise() is the supported equivalent here).
      const payload = await this.surveyMetadataService
        .getApplicationSurveyMetadata(this.rentalApplicationKey)
        .pipe(take(1))
        .toPromise();
      return payload ?? null;
    } catch (err) {
      console.warn('[PaymentComponent] survey metadata fetch failed; cookie skipped', err);
      return null;
    }
  }

  /**
   * TP-9217: On payment success, await the in-flight metadata fetch, write
   * the Medallia bridge cookie iff a payload arrived, then redirect. The
   * fetch never rejects, so the redirect always happens. The cookie is
   * only written for actually-completed payments, so users who close the
   * modal never leave a stale cookie behind.
   */
  private writeSurveyCookieThenRedirect(redirectURL: string): void {
    const pending = Promise.race([this.surveyMetadataPromise ?? Promise.resolve(null), new Promise<null>(resolve => setTimeout(() => resolve(null), 5000))]) as Promise<unknown | null>;
    // TP-9217 DIAGNOSTIC: non-prod logs to confirm cookie write path end-to-end
    // during staging E2E. Remove (or the gating) once the marketing-site reader
    // is verified against the cookie.
    if (!environment.production) {
      console.log('[TP-9217] writeSurveyCookieThenRedirect start', {
        hasPromise: !!this.surveyMetadataPromise,
        redirectURL,
        hostname: window.location.hostname,
        protocol: window.location.protocol,
        cookieEnabled: navigator.cookieEnabled
      });
    }
    pending
      .catch(err => {
        console.warn('[TP-9217] survey metadata promise rejected (unexpected)', err);
        return null;
      })
      .then(payload => {
        if (!environment.production) {
          console.log('[TP-9217] resolved payload', {
            payloadType: typeof payload,
            isNull: payload === null,
            isUndefined: payload === undefined,
            isTruthy: !!payload,
            keys: payload && typeof payload === 'object' ? Object.keys(payload as object) : null
          });
        }
        if (payload) {
          const cookieBefore = document.cookie;
          this.surveyCookieService.writeApplicationSurveyCookie(payload);
          const cookieAfter = document.cookie;
          if (!environment.production) {
            console.log('[TP-9217] cookie write attempted', {
              wroteSomething: cookieBefore !== cookieAfter,
              cookieAfterContainsName: cookieAfter.includes('tricon_application_survey'),
              // Don't log the full cookie value (PII risk); just length + presence.
              allCookieNames: cookieAfter.split(';').map(c => c.split('=')[0].trim())
            });
          }
        } else if (!environment.production) {
          console.warn('[TP-9217] payload was falsy; skipping cookie write');
        }
        localStorage.clear();
        if (!environment.production) {
          console.log('[TP-9217] navigating to', redirectURL);
        }
        window.location.href = redirectURL;
      });
  }

  openCheckout() {
    // TP-9217: Kick off the survey metadata fetch in parallel with the Stripe
    // modal so its latency is hidden behind card entry. The cookie itself is
    // only written on payment success in writeSurveyCookieThenRedirect, so
    // abandoning the modal leaves no stale cookie behind.
    this.surveyMetadataPromise = this.startSurveyMetadataFetch();

    const handler = (<any>window).StripeCheckout.configure({
      key: this.stripePublishableKey,
      image: '/assets/images/favicon.png',
      src: 'https://checkout.stripe.com/checkout.js',
      locale: 'auto',
      zipCode: true,
      billingAddress: true,
      token: this.processCharge.bind(this)
    });

    this.analyticsService.track(
      'HRA Application',
      'Payment Information',
      'Checkout',
      'Not the primary resident, sending to basic information',
      '/application/basic',
      this.additionalAnalyticsDetails());
    handler.open({
      name: 'Pay All Fees',
      description: this.paymentCheckoutFormMessage,
      // Stripe service expects ChargeAmount as an integer with cents as whole numbers. For example $250 should be 25000.
      amount: this.modelApplicationFee.TotalApplicationFee * 100
    });

    // Close Checkout on page navigation:
    window.addEventListener('popstate', function () {
      handler.close()
    });
  }

  handleWaitListCheck(IsWaitListed: boolean) {
    if (IsWaitListed) {
      this.analyticsService.track(
        'HRA Application',
        'Payment Information',
        'Stop',
        'Application is waitlisted',
        '/application/dashboard',
        this.additionalAnalyticsDetails());
      this.router.navigate(['/application/dashboard', 'waitlist']);
    }
  }

  applyCoupon() {
    this.paymentService.checkCouponValidity(this.couponCode)
      .subscribe(res => this.couponValidityResponse(res), err => this.handleError(err));
  }

  couponValidityResponse(coupResponse: CouponValidity) {
    this.coupon = coupResponse;

    if (this.coupon.Valid == true) {
      this.paymentService.getApplicationFeeWithDetails(this.rentalApplicationKey, this.couponCode)
        .subscribe(res => this.setIncompleteApplicantsWarningMessage(res), err => this.handleError(err));
    }
    else {
      this.couponCode = '';
    }

  }
}
